<app-menu class="paul-menu" [class.paul-menu-light]="!isDark">
<div class="paul-page" [class.light-theme]="!isDark">
  <main class="paul-shell">
    <header class="page-header">
      <div>
        <div class="eyebrow">Admin Tools / PAUL</div>
        <h1>PAUL Chat</h1>
        <p>Ask a real freight question, inspect what PAUL used, and capture a structured correction.</p>
      </div>
      <div class="header-actions">
        <span class="access-pill"><i class="fa-solid fa-lock"></i> Superadmin only</span>
        <button class="ghost-button" type="button" (click)="openCoachingData()">
          <i class="fa-solid fa-table-list"></i> Coaching Data
        </button>
        <button class="icon-button" type="button" (click)="toggleTheme()" aria-label="Toggle theme">
          <i class="fa-solid" [class.fa-sun]="isDark" [class.fa-moon]="!isDark"></i>
        </button>
      </div>
    </header>

    <div class="notice error" *ngIf="errorMessage">{{ errorMessage }}</div>
    <div class="notice success" *ngIf="successMessage">{{ successMessage }}</div>

    <section class="workspace-grid">
      <article class="chat-card">
        <div class="card-heading">
          <div>
            <span class="section-kicker">Server-persisted context</span>
            <h2>Conversation</h2>
          </div>
          <div class="conversation-actions">
            <span class="run-pill" *ngIf="conversationUuid">
              {{ conversationTurnCount }} {{ conversationTurnCount === 1 ? 'turn' : 'turns' }} · {{ conversationUuid | slice:0:8 }}
            </span>
            <button class="small-button" type="button" (click)="newConversation()">
              <i class="fa-solid fa-plus"></i> New
            </button>
          </div>
        </div>

        <div class="conversation" [class.empty]="turns.length === 0">
          <div class="empty-state" *ngIf="turns.length === 0">
            <div class="paul-mark">P</div>
            <h3>Ask PAUL for a freight rate</h3>
            <p>Every answer on this screen is stored with its tool trace so it can be reviewed and coached.</p>
          </div>

          <div
            class="turn"
            *ngFor="let turn of turns"
            [class.user-turn]="turn.role === 'user'"
            [class.paul-turn]="turn.role === 'paul'"
            [class.selected]="turn.runUuid === selectedRun?.coach_run_uuid"
            [attr.role]="turn.role === 'paul' ? 'button' : null"
            [attr.tabindex]="turn.role === 'paul' ? 0 : null"
            [attr.aria-pressed]="turn.role === 'paul' ? (turn.runUuid === selectedRun?.coach_run_uuid) : null"
            (click)="selectRun(turn)"
            (keydown.enter)="selectRun(turn)"
            (keydown.space)="selectRun(turn); $event.preventDefault()"
          >
            <span class="turn-label">{{ turn.role === 'user' ? 'You' : 'PAUL' }}</span>
            <span class="turn-text">{{ turn.text }}</span>
            <span class="turn-meta" *ngIf="turn.runUuid">
              {{ turn.model || 'model unavailable' }} · {{ turn.latencyMs | number }} ms ·
              {{ turn.toolCount || 0 }} tools
            </span>
          </div>

          <div class="thinking" *ngIf="restoringConversation">
            <span></span><span></span><span></span> Restoring the previous conversation…
          </div>

          <div class="thinking" *ngIf="asking">
            <span></span><span></span><span></span> PAUL is checking the available freight data…
          </div>
        </div>

        <div class="composer">
          <textarea
            [(ngModel)]="prompt"
            (keydown)="handlePromptKeydown($event)"
            [disabled]="asking || restoringConversation"
            rows="3"
            placeholder="Example: 60494 to 97524, fitness equipment, 561 lbs. Any rate you have."
          ></textarea>
          <div class="composer-footer">
            <span>Enter to send · Shift+Enter for a new line</span>
            <button class="primary-button" type="button" (click)="ask()" [disabled]="asking || restoringConversation || !prompt.trim()">
              <i class="fa-solid fa-paper-plane"></i> {{ asking ? 'Working…' : 'Ask PAUL' }}
            </button>
          </div>
        </div>
      </article>

      <aside class="coach-card">
        <div class="card-heading">
          <div>
            <span class="section-kicker">Human review</span>
            <h2>Coach This Answer</h2>
          </div>
          <span class="run-pill" *ngIf="selectedRun">{{ selectedRun.coach_run_uuid | slice:0:8 }}</span>
        </div>

        <div class="empty-coach" *ngIf="!selectedRun">
          Select a PAUL answer to record a correction and supporting evidence.
        </div>

        <form *ngIf="selectedRun" (ngSubmit)="submitCoaching()">
          <div class="form-row three">
            <label>Lesson type
              <select [(ngModel)]="lessonType" name="lessonType">
                <option *ngFor="let type of lessonTypes" [value]="type">{{ type.replace('_', ' ') }}</option>
              </select>
            </label>
            <label>Severity
              <select [(ngModel)]="severity" name="severity">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </label>
            <label>Confidence
              <select [(ngModel)]="coachConfidence" name="coachConfidence">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </label>
          </div>

          <label>Short title
            <input [(ngModel)]="title" name="title" placeholder="What should we remember?">
          </label>
          <label>What was wrong?
            <textarea [(ngModel)]="correction" name="correction" rows="3" placeholder="Describe the business problem in plain language."></textarea>
          </label>
          <label>What should PAUL do next time?
            <textarea [(ngModel)]="expectedBehavior" name="expectedBehavior" rows="3"></textarea>
          </label>
          <label>Example of the correct answer
            <textarea [(ngModel)]="expectedAnswer" name="expectedAnswer" rows="3"></textarea>
          </label>
          <div class="form-row two">
            <label>Source of truth
              <input [(ngModel)]="sourceOfTruth" name="sourceOfTruth" placeholder="Rate card, settlement, customer rule…">
            </label>
            <label>Review status
              <select [(ngModel)]="verificationStatus" name="verificationStatus">
                <option value="pending">Pending verification</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
                <option value="unverified">Unverified</option>
              </select>
            </label>
          </div>
          <label>Evidence
            <textarea [(ngModel)]="evidenceNote" name="evidenceNote" rows="2" placeholder="Quote number, lane example, policy, or where it was confirmed."></textarea>
          </label>
          <details class="technical-notes">
            <summary>Technical diagnosis and internal notes</summary>
            <label>Technical diagnosis
              <textarea [(ngModel)]="technicalDiagnosis" name="technicalDiagnosis" rows="3"></textarea>
            </label>
            <label>Business notes
              <textarea [(ngModel)]="businessNotes" name="businessNotes" rows="2"></textarea>
            </label>
          </details>
          <button class="primary-button full" type="submit" [disabled]="coaching">
            <i class="fa-solid fa-graduation-cap"></i> {{ coaching ? 'Saving…' : 'Save Coaching Lesson' }}
          </button>
        </form>

        <details class="trace-panel" *ngIf="selectedRun">
          <summary>Technical response stored with this run</summary>
          <div class="trace-chips">
            <span *ngFor="let tool of selectedRun.trace_summary.tools">{{ tool }}</span>
          </div>
          <pre>{{ pretty(selectedRun.chat_result) }}</pre>
        </details>
      </aside>
    </section>
  </main>
</div>
</app-menu>
